import { motion } from "framer-motion";
import { Phone, MapPin, Clock } from "lucide-react";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const ContactInfoCard = ({ icon: Icon, title, content, href }) => (
  <motion.div variants={fadeInUp} className="flex items-start gap-4">
    <div className="w-12 h-12 bg-[#0F4C81]/10 rounded-full flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-[#0F4C81]" />
    </div>
    <div>
      <h3 className="font-heading text-lg font-medium text-[#1A202C] mb-1">{title}</h3>
      {href ? (<a href={href} className="font-body text-[#4B5563] hover:text-[#0F4C81] transition-colors">{content}</a>) : (<p className="font-body text-[#4B5563]">{content}</p>)}
    </div>
  </motion.div>
);

const ContactPage = () => {
  return (
    <div className="pt-20">
      <SEO
        title="Contact Us"
        description="Contact Brunswick Glass for a free quote on custom shower glass, window replacement, mirrors, and commercial glass in coastal NC. Call 910-457-4757 or fill out our form."
        keywords="contact Brunswick Glass, glass quote NC, free glass estimate, Brunswick County glass company, Wilmington glass services, 910-457-4757"
      />
      
      {/* Hero */}
      <section className="relative min-h-[40vh] flex items-center bg-[#F9F9F7]">
        <div className="section-container relative z-10 w-full py-20">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.span variants={fadeInUp} className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4">Contact Us</motion.span>
            <motion.h1 variants={fadeInUp} className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-6">Get In Touch</motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-xl leading-relaxed text-[#4B5563]">Ready to start your project? Contact us for a free consultation and quote.</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="font-heading text-3xl font-medium text-[#1A202C] mb-8">Contact Information</motion.h2>
              <div className="space-y-8">
                <ContactInfoCard icon={Phone} title="Phone" content="910-457-4757" href="tel:910-457-4757" />
                <ContactInfoCard icon={MapPin} title="Service Area" content="Coastal North Carolina including Brunswick County, Wilmington, Southport, Oak Island, and surrounding areas" />
                <ContactInfoCard icon={Clock} title="Business Hours" content="Monday - Friday: 8:00 AM - 5:00 PM" />
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-[#F9F9F7] p-8 md:p-12">
              <h2 className="font-heading text-3xl font-medium text-[#1A202C] mb-2">Request a Quote</h2>
              <p className="font-body text-[#4B5563] mb-8">Fill out the form below and we'll get back to you within 24 hours.</p>
              <QuoteForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="section-spacing bg-[#F9F9F7]">
        <div className="section-container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-12">
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">Areas We Serve</motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#4B5563] max-w-2xl mx-auto">We proudly serve homeowners and businesses throughout coastal North Carolina.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-4 gap-4">
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Brunswick County</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Wilmington</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Southport</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Oak Island</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Leland</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Shallotte</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Calabash</span></motion.div>
            <motion.div variants={fadeInUp} className="bg-white p-4 text-center"><span className="font-body font-medium text-[#1A202C]">Holden Beach</span></motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F4C81]">
        <div className="section-container text-center">
          <h2 className="font-heading text-3xl font-medium text-white mb-4">Prefer to Call?</h2>
          <p className="font-body text-lg text-white/80 mb-6">Speak directly with our team for immediate assistance.</p>
          <a href="tel:910-457-4757" className="inline-flex items-center gap-2 bg-white text-[#0F4C81] px-8 py-4 rounded-full font-body font-semibold hover:bg-white/90 transition-colors"><Phone className="w-5 h-5" />910-457-4757</a>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
